import React, { useEffect, useState, useRef } from "react";
import { Card, Button, Input, Select, Checkbox, Spin, App } from "antd";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import theme from "../../themes";
import { pageShellCss, pageTitleCss, wrapRowCss } from "../../styles/responsive";
import { useAppDispatch, useAppSelector } from "../../store";
import { getUserInfor, updateUserInfor } from "../../store/actions/auth.action";
import { IUpdateUser } from "../../models/auth.model";
import ToastMessage from "../../utils/toastMessage";
import { getLanguageCode } from "../../utils/languageUtils";
import { useForm as useHookForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ReactComponent as CheckCircleIcon } from "../../assets/icons/checkCircle.svg";
import { ReactComponent as EditIcon } from "../../assets/icons/edit.svg";

const { Option } = Select;

type ProfileFormValues = {
  full_name: string;
  email: string;
  language: string;
  changePassword?: boolean | undefined;
  old_password: string | undefined;
  password: string | undefined;
  confirm_password: string | undefined;
  is_enabled_2FA: boolean | undefined;
};

const Profile: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [editProfile, setEditProfile] = useState(false);
  const dispatch = useAppDispatch();
  const [user, setUser] = useState<IUpdateUser | null>(null);
  const { loading } = useAppSelector((state) => state.auth);
  const { message } = App.useApp();
  const toast = ToastMessage(message);

  // Languages with translations
  const LANGUAGES = [
    { value: t("language.english"), label: t("language.english"), code: "en" },
    { value: t("language.chinese"), label: t("language.chinese"), code: "zh" },
    { value: t("language.spanish"), label: t("language.spanish"), code: "es" },
  ];

  // Validation schema with translations
  const profileSchema = yup.object().shape({
    full_name: yup.string().required(t("profile.validation.nameRequired")),
    email: yup
      .string()
      .email(t("profile.validation.emailInvalid"))
      .required(t("profile.validation.emailRequired")),
    language: yup.string().required(t("profile.validation.languageRequired")),
    changePassword: yup.boolean(),
    old_password: yup.string().when("changePassword", {
      is: true,
      then: (schema) =>
        schema.required(t("profile.validation.oldPasswordRequired")),
      otherwise: (schema) => schema.notRequired(),
    }),
    password: yup.string().when("changePassword", {
      is: true,
      then: (schema) =>
        schema.required(t("profile.validation.newPasswordRequired")),
      otherwise: (schema) => schema.notRequired(),
    }),
    confirm_password: yup.string().when("changePassword", {
      is: true,
      then: (schema) =>
        schema
          .required(t("profile.validation.confirmPasswordRequired"))
          .oneOf(
            [yup.ref("password")],
            t("profile.validation.passwordsDoNotMatch")
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
    is_enabled_2FA: yup.boolean(),
  });

  const fetchUserInfor = async () => {
    try {
      const res = await dispatch(getUserInfor()).unwrap();
      setUser(res.data);
    } catch (error) {
      console.error("Error fetching user information:", error);
    }
  };

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useHookForm({
    resolver: yupResolver(profileSchema),
    defaultValues: {
      full_name: user?.full_name || "",
      email: user?.email || "",
      language: user?.language || "English",
      changePassword: false,
      old_password: "",
      password: "",
      confirm_password: "",
      is_enabled_2FA: user?.is_enabled_2FA || false,
    },
  });

  const watchedChangePassword = watch("changePassword");

  useEffect(() => {
    if (user) {
      reset({
        full_name: user.full_name || "",
        email: user.email || "",
        language: user.language || "English",
        changePassword: false,
        old_password: "",
        password: "",
        confirm_password: "",
        is_enabled_2FA: user.is_enabled_2FA || false,
      });
    }
  }, [user, reset]);

  // Handlers
  const handleEditProfile = () => {
    setEditProfile(true);
  };
  const handleCancelProfile = () => {
    setEditProfile(false);
    reset(); // Use reset() from react-hook-form
  };
  const handleSaveProfile = handleSubmit(async (values) => {
    const changedFields: Partial<IUpdateUser> = {};
    (Object.keys(values) as (keyof ProfileFormValues)[]).forEach((key) => {
      if (key === "confirm_password" || key === "changePassword") {
        return;
      }
      if (key === "old_password" || key === "password") {
        if (values[key]) {
          (changedFields as any)[key] = values[key] as string;
        }
      } else if (values[key] !== (user as any)?.[key]) {
        (changedFields as any)[key] = values[key] as string;
      }
    });
    if (Object.keys(changedFields).length === 0) {
      setEditProfile(false);
      return;
    }
    // Change i18n language using the language code mapping
    const languageCode = getLanguageCode(values.language);
    i18n.changeLanguage(languageCode);
    try {
      await dispatch(updateUserInfor(changedFields)).unwrap();
      setEditProfile(false);
      // Update language from profile after successful update
      toast.success(
        t("profile.profileUpdatedSuccessfully"),
        5,
        <CheckCircleIcon />
      );
    } catch (error) {
      toast.error(t("profile.errorSavingProfileChanges"), 5);
    } finally {
      fetchUserInfor();
    }
  });

  useEffect(() => {
    fetchUserInfor();
  }, []);

  const formRef = useRef<HTMLFormElement>(null);

  return (
    <Container>
      <Title>{t("profile.title")}</Title>
      <SectionCard>
        <SectionHeader>
          <SectionTitle>{t("profile.personalProfile")}</SectionTitle>
          {editProfile ? (
            <div>
              <Button
                size="large"
                onClick={handleCancelProfile}
                style={{ marginRight: 8 }}
              >
                {t("profile.cancel")}
              </Button>
              <Button
                size="large"
                type="primary"
                onClick={() => formRef.current?.requestSubmit()}
                style={{
                  background: theme.colors.green1,
                  borderColor: theme.colors.green1,
                }}
              >
                {t("profile.save")}
              </Button>
            </div>
          ) : (
            <Button
              type="text"
              icon={<EditIcon />}
              onClick={handleEditProfile}
            />
          )}
        </SectionHeader>
        {editProfile ? (
          <form
            ref={formRef}
            onSubmit={handleSaveProfile}
            style={{ marginTop: 8 }}
          >
            <InfoGrid>
              <InfoRow>
                <Label>{t("profile.name")}</Label>
                <div style={{ flex: 1 }}>
                  <Controller
                    name="full_name"
                    control={control}
                    render={({ field }) => (
                      <Input {...field} allowClear size="large" />
                    )}
                  />
                  {errors.full_name && (
                    <div style={{ color: "red" }}>
                      {errors.full_name.message}
                    </div>
                  )}
                </div>
              </InfoRow>
              <InfoRow>
                <Label>{t("profile.email")}</Label>
                <div style={{ flex: 1 }}>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <Input {...field} allowClear size="large" />
                    )}
                  />
                  {errors.email && (
                    <div style={{ color: "red" }}>{errors.email.message}</div>
                  )}
                </div>
              </InfoRow>
              <InfoRow>
                <Label>{t("profile.password")}</Label>
                <div style={{ flex: 1 }}>
                  <Controller
                    name="changePassword"
                    control={control}
                    render={({ field }) => (
                      <StyledCheckbox>
                        <Checkbox
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          style={{ fontSize: 16 }}
                        >
                          {t("profile.changePassword")}
                        </Checkbox>
                      </StyledCheckbox>
                    )}
                  />
                </div>
              </InfoRow>

              {watchedChangePassword && (
                <>
                  <InfoRow>
                    <Label>{t("profile.oldPassword")}</Label>
                    <div style={{ flex: 1 }}>
                      <Controller
                        name="old_password"
                        control={control}
                        render={({ field }) => (
                          <Input.Password
                            {...field}
                            size="large"
                            placeholder={t("profile.enterOldPassword")}
                            autoComplete="current-password"
                          />
                        )}
                      />
                      {errors.old_password && (
                        <div style={{ color: "red" }}>
                          {errors.old_password.message}
                        </div>
                      )}
                    </div>
                  </InfoRow>
                  <InfoRow>
                    <Label>{t("profile.newPassword")}</Label>
                    <div style={{ flex: 1 }}>
                      <Controller
                        name="password"
                        control={control}
                        render={({ field }) => (
                          <Input.Password
                            {...field}
                            size="large"
                            placeholder={t("profile.enterNewPassword")}
                            autoComplete="new-password"
                          />
                        )}
                      />
                      {errors.password && (
                        <div style={{ color: "red" }}>
                          {errors.password.message}
                        </div>
                      )}
                    </div>
                  </InfoRow>
                  <InfoRow>
                    <Label>{t("profile.confirmNewPassword")}</Label>
                    <div style={{ flex: 1 }}>
                      <Controller
                        name="confirm_password"
                        control={control}
                        render={({ field }) => (
                          <Input.Password
                            {...field}
                            size="large"
                            placeholder={t("profile.reEnterNewPassword")}
                            autoComplete="new-password"
                          />
                        )}
                      />
                      {errors.confirm_password && (
                        <div style={{ color: "red" }}>
                          {errors.confirm_password.message}
                        </div>
                      )}
                    </div>
                  </InfoRow>
                </>
              )}
              <InfoRow>
                <Label>{t("profile.language")}</Label>
                <div style={{ flex: 1 }}>
                  <Controller
                    name="language"
                    control={control}
                    render={({ field }) => (
                      <Select {...field} size="large">
                        {LANGUAGES.map((l) => (
                          <Option key={l.value} value={l.value}>
                            {l.label}
                          </Option>
                        ))}
                      </Select>
                    )}
                  />
                  {errors.language && (
                    <div style={{ color: "red" }}>
                      {errors.language.message}
                    </div>
                  )}
                </div>
              </InfoRow>
              <InfoRow>
                <Label>{t("profile.multiFactorAuthentication")}</Label>
                <div style={{ flex: 1 }}>
                  <Controller
                    name="is_enabled_2FA"
                    control={control}
                    render={({ field }) => (
                      <StyledCheckbox>
                        <Checkbox
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          style={{ fontSize: 16 }}
                        >
                          {t("profile.enable")}
                        </Checkbox>
                      </StyledCheckbox>
                    )}
                  />
                </div>
              </InfoRow>
            </InfoGrid>
          </form>
        ) : (
          <InfoGrid>
            <InfoRow>
              <Label>{t("profile.name")}</Label>
              <Value>{user?.full_name}</Value>
            </InfoRow>
            <InfoRow>
              <Label>{t("profile.email")}</Label>
              <Value>{user?.email}</Value>
            </InfoRow>
            <InfoRow>
              <Label>{t("profile.password")}</Label>
              <Value>***************</Value>
            </InfoRow>
            <InfoRow>
              <Label>{t("profile.language")}</Label>
              <Value>{user?.language}</Value>
            </InfoRow>
            <InfoRow>
              <Label>{t("profile.multiFactorAuthentication")}</Label>
              <Value>
                {user?.is_enabled_2FA
                  ? t("profile.enabled")
                  : t("profile.disabled")}
              </Value>
            </InfoRow>
          </InfoGrid>
        )}
      </SectionCard>
      {loading && (
        <LoadingOverlay>
          <Spin size="large" />
        </LoadingOverlay>
      )}
    </Container>
  );
};

export default Profile;

const Container = styled.div`
  width: 100%;
  background: ${theme.colors.grey3};
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${pageShellCss}
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  min-height: 40px;
  margin: 0;
  ${pageTitleCss}
`;

const SectionHeader = styled.div`
  ${wrapRowCss}
  margin-bottom: 16px;
  min-height: 40px;
`;

const SectionCard = styled(Card)`
  border-radius: 12px;
  margin-bottom: 16px;
`;

const SectionTitle = styled.div`
  font-size: 18px;
  font-weight: 500;
  color: ${theme.colors.grey1};
`;

const InfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const InfoRow = styled.div`
  display: flex;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const Label = styled.div`
  width: 260px;
  font-size: 16px;
  font-weight: 400;
  align-items: center;
  display: flex;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: auto;
  }
`;

const Value = styled.div`
  color: #222;
  font-size: 16px;
  font-weight: 400;
  align-items: center;
  display: flex;
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(
    255,
    255,
    255,
    0.8
  ); /* Semi-transparent white background */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000; /* Ensure it's above other content */
  gap: 16px;
`;

const StyledCheckbox = styled.div`
  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: ${theme.colors.green1} !important;
    border-color: ${theme.colors.green1} !important;
  }
  .ant-checkbox:hover .ant-checkbox-inner,
  .ant-checkbox-input:focus + .ant-checkbox-inner {
    border-color: ${theme.colors.green1} !important;
  }
`;
